export const calculateMatchScore = (profile1, profile2) => {
  let score = 0;
  let factors = 0;

  // Role complementarity (highest weight)
  const roleCompatibility = getRoleCompatibility(profile1.role, profile2.role);
  score += roleCompatibility * 0.25;
  factors += 0.25;

  // Needs vs Offers matching
  const needsOffersScore = calculateNeedsOffersMatch(profile1, profile2);
  score += needsOffersScore * 0.25;
  factors += 0.25;

  // Matching preferences compatibility
  const preferencesScore = calculatePreferencesMatch(profile1, profile2);
  score += preferencesScore * 0.2;
  factors += 0.2;

  // Platform overlap
  const platformScore = calculateArrayOverlap(profile1.platforms || [], profile2.platforms || []);
  score += platformScore * 0.15;
  factors += 0.15;

  // Genre overlap
  const genreScore = calculateArrayOverlap(profile1.genres || [], profile2.genres || []);
  score += genreScore * 0.15;
  factors += 0.15;

  return factors > 0 ? score / factors : 0;
};

const getRoleCompatibility = (role1, role2) => {
  const compatibilityMatrix = {
    'Developer': {
      'Publisher': 0.9,
      'Investor': 0.8,
      'Mentor': 0.7,
      'Developer': 0.5
    },
    'Publisher': {
      'Developer': 0.9,
      'Investor': 0.6,
      'Mentor': 0.4,
      'Publisher': 0.3
    },
    'Investor': {
      'Developer': 0.8,
      'Publisher': 0.6,
      'Mentor': 0.3,
      'Investor': 0.2
    },
    'Mentor': {
      'Developer': 0.7,
      'Publisher': 0.4,
      'Investor': 0.3,
      'Mentor': 0.2
    }
  };

  return compatibilityMatrix[role1]?.[role2] || 0;
};

const calculateNeedsOffersMatch = (profile1, profile2) => {
  const needs1 = profile1.needs || [];
  const offers1 = profile1.offers || [];
  const needs2 = profile2.needs || [];
  const offers2 = profile2.offers || [];

  const match1to2 = calculateArrayOverlap(needs1, offers2);
  const match2to1 = calculateArrayOverlap(needs2, offers1);

  return (match1to2 + match2to1) / 2;
};

const calculatePreferencesMatch = (profile1, profile2) => {
  let score = 0;
  let factors = 0;

  // Check if profile2's role matches profile1's preferred partner roles
  const preferredRoles1 = profile1.preferredPartnerRoles || [];
  const preferredRoles2 = profile2.preferredPartnerRoles || [];
  
  if (preferredRoles1.length > 0) {
    score += preferredRoles1.includes(profile2.role) ? 1 : 0;
    factors += 1;
  }
  
  if (preferredRoles2.length > 0) {
    score += preferredRoles2.includes(profile1.role) ? 1 : 0;
    factors += 1;
  }

  // Experience level compatibility
  const expLevel1 = profile1.preferredExperienceLevel || [];
  const expLevel2 = profile2.preferredExperienceLevel || [];
  
  if (expLevel1.length > 0 && expLevel2.length > 0) {
    const expOverlap = calculateArrayOverlap(expLevel1, expLevel2);
    score += expOverlap;
    factors += 1;
  }

  // Company size compatibility
  const companySize1 = profile1.preferredCompanySize || [];
  const companySize2 = profile2.preferredCompanySize || [];
  
  if (companySize1.length > 0 && companySize2.length > 0) {
    const sizeOverlap = calculateArrayOverlap(companySize1, companySize2);
    score += sizeOverlap;
    factors += 1;
  }

  // Project stage compatibility
  const projectStages1 = profile1.preferredProjectStages || [];
  const projectStages2 = profile2.preferredProjectStages || [];
  
  if (projectStages1.length > 0 && projectStages2.length > 0) {
    const stageOverlap = calculateArrayOverlap(projectStages1, projectStages2);
    score += stageOverlap;
    factors += 1;
  }

  return factors > 0 ? score / factors : 0;
};

const calculateArrayOverlap = (arr1, arr2) => {
  if (!arr1.length || !arr2.length) return 0;
  
  const intersection = arr1.filter(item => arr2.includes(item));
  const union = [...new Set([...arr1, ...arr2])];
  
  return intersection.length / union.length;
};

export const getMatchingProfiles = (currentProfile, allProfiles, matches) => {
  const matchedProfiles = matches
    .filter(match => 
      match.profile1Id === currentProfile.id || match.profile2Id === currentProfile.id
    )
    .map(match => {
      const otherProfileId = match.profile1Id === currentProfile.id 
        ? match.profile2Id 
        : match.profile1Id;
      
      const otherProfile = allProfiles.find(p => p.id === otherProfileId);
      return otherProfile ? { ...otherProfile, matchScore: match.score } : null;
    })
    .filter(Boolean)
    .sort((a, b) => b.matchScore - a.matchScore);

  return matchedProfiles;
};